import type React from "react";
import { useMemo } from "react";

import { cx, styled } from "simplydo/react/styled";
import { computerUp, mobileOnly, tabletUp } from "simplydo/react/theme";
import {
  Dropdown,
  Loader,
  Popup,
  type SemanticICONS,
  Icon as SemanticIcon,
} from "simplydo/react/ui";

import type { HoverActionProps } from "./types";

export const HoverDropdown = styled(Dropdown)`
  &&&&& {
    .menu {
      position: absolute;
      right: 0;
      left: auto;
      max-width: 250px;
      overflow: hidden;
      min-width: 0;

      .item {
        overflow: hidden;
        max-width: 100%;
        text-overflow: ellipsis;
      }
    }
  }
`;

export const HoverActions = styled.div<{ $width?: number }>`
  position: absolute;
  padding: 10px 10px;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: transparent;
  right: 100%;
  min-width: 300px;
  max-height: 100%;
  overflow: hidden;
  .hover-actions-inner {
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: flex-end;
    width: 100%;
    overflow: hidden;
  }

  ${computerUp} {
    width: ${({ $width }) => ($width ? `calc(100vw - ${$width}px)` : "300px")};
  }
`;

export const HoverActionButton = styled.div<{ $margined?: boolean }>`
  user-select: none;
  pointer-events: auto;
  background-color: rgba(18, 19, 19, 0.9);
  cursor: pointer;
  display: flex;
  border-radius: 5px;
  padding: 7px;
  flex-direction: row;
  align-items: center;
  margin-bottom: 10px;
  margin-top: ${({ $margined }) => ($margined ? "30px" : null)};
  i {
    margin-left: 3px;
    margin-bottom: 4px;
    color: #fff;
  }
  span {
    color: #fff;
    display: block;
    font-size: 19px;
  }
`;

const HoverActionContainerStyled = styled(HoverActionButton)`
  &.is-disabled {
    filter: contrast(0.6);
    cursor: default;
  }

  &:not(.is-disabled) {
    &:hover {
      opacity: 0.6;
    }
    transition-property: opacity;
    transition-duration: 0.1s;
    transition-timing-function: ease-out;
  }
`;

type HoverActionContainerProps = React.ComponentProps<
  typeof HoverActionContainerStyled
> & { $disabled?: boolean };

export const HoverActionContainer = ({
  $disabled,
  className,
  ...props
}: HoverActionContainerProps) => (
  <HoverActionContainerStyled
    className={cx($disabled && "is-disabled", className)}
    {...props}
  />
);

const HoverActionsContentBoxStyled = styled.div`
  max-height: 100%;
  pointer-events: auto;
  margin-bottom: 10px;

  &.is-margined {
    margin-top: 30px;

    ${mobileOnly} {
      margin-top: 15px;
    }
  }

  ${mobileOnly} {
    &:not(.is-margined) {
      margin-top: 30px;
    }
  }

  &.is-open {
    width: 100%;
  }

  ${HoverActionButton} {
    margin-bottom: 0;
    justify-content: space-between;
  }

  &.is-open ${HoverActionButton} {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  &.no-click ${HoverActionButton} {
    cursor: default;
  }

  &:not(.no-click) ${HoverActionButton} i {
    cursor: pointer;
  }

  .fake-icon {
    width: 16px;
  }

  &.compress-accordions {
    &&&& {
      .accordion .title,
      .accordion .content {
        padding: 0;
      }
    }
  }

  .content-box {
    display: flex;
    flex-direction: column;
    background-color: #fff;
    min-height: 300px;
    padding: 10px;
    border-bottom-right-radius: 5px;
    border-bottom-left-radius: 5px;

    ${tabletUp} {
      max-height: 500px;
    }

    .title {
      font-weight: 600;
      display: block;
      margin-bottom: 10px;
    }
    > .content-item {
      display: flex;
      margin-bottom: 10px;
    }
  }

  &.is-large .content-box {
    ${tabletUp} {
      max-height: 100%;
    }
  }
`;

type HoverActionsContentBoxProps = React.ComponentProps<
  typeof HoverActionsContentBoxStyled
> & {
  $large?: boolean;
  $margined?: boolean;
  $isOpen?: boolean;
  $noClick?: boolean;
  $compressAccordions?: boolean;
};

export const HoverActionsContentBox = ({
  $large,
  $margined,
  $isOpen,
  $noClick,
  $compressAccordions,
  className,
  ...props
}: HoverActionsContentBoxProps) => (
  <HoverActionsContentBoxStyled
    className={cx(
      $large && "is-large",
      $margined && "is-margined",
      $isOpen && "is-open",
      $noClick && "no-click",
      $compressAccordions && "compress-accordions",
      className,
    )}
    {...props}
  />
);

export const HoverAction = ({
  children,
  icon,
  onClick,
  disabled,
  margined,
  target,
  rel,
  popupStyle,
  popupContent,
  popupPosition,
  style,
  loading,
  href,
  download,
  as,
}: HoverActionProps) => {
  const HoverActionComponent = useMemo(
    () => (
      <HoverActionContainer
        onClick={disabled ? undefined : onClick}
        href={href}
        target={target}
        rel={rel}
        as={as}
        download={download}
        $disabled={disabled}
        $margined={margined}
        style={style}
      >
        <span>{children}</span>
        <div style={{ marginLeft: children ? 7 : 0 }}>
          {loading ?
            <Loader
              size="tiny"
              active
              inline="centered"
              style={{ marginLeft: 4, marginRight: 3 }}
            />
          : icon && <SemanticIcon name={icon} />}
        </div>
      </HoverActionContainer>
    ),
    [
      children,
      disabled,
      icon,
      margined,
      onClick,
      style,
      loading,
      href,
      download,
      as,
      target,
      rel,
    ],
  );

  if (popupContent) {
    return (
      <Popup
        trigger={HoverActionComponent}
        content={popupContent}
        style={popupStyle}
        on="hover"
        hoverable
        disabled={disabled}
        position={popupPosition}
      />
    );
  }
  return HoverActionComponent;
};
